import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("checking"); // checking | authed | anon
  const [needsProfile, setNeedsProfile] = useState(false);

  const refresh = useCallback(async () => {
    setStatus("checking");
    try {
      const { data } = await api.get("/auth/me");
      if (data?.authenticated) {
        setUser(data.user);
        // Profile required ONLY if pseudonym is missing or too short
        const hasPseudonym = data.user?.pseudonym && data.user.pseudonym.trim().length >= 2;
        setNeedsProfile(!hasPseudonym);
        setStatus("authed");
      } else {
        setUser(null);
        setNeedsProfile(false);
        setStatus("anon");
      }
    } catch {
      setUser(null);
      setNeedsProfile(false);
      setStatus("anon");
    }
  }, []);

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    setNeedsProfile(false);
    setStatus("anon");
  };

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ 
      user, 
      status, 
      needsProfile, 
      setNeedsProfile, 
      refresh, 
      logout,
      isAuthenticated: status === "authed",  // Add convenience property
    }),
    [user, status, needsProfile, refresh],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
